'use client';

import { CheckCircle, Loader2, AlertCircle, Folder, Plus, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';

/**
 * Tool UI for Create Collection tool
 * Shows collection creation success with details
 */
export function CreateCollectionUI({ part, status }: any) {
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Creating collection...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Failed to create collection
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  const collection = result.collection || {};
  const collectionName = collection.name || result.name || 'Untitled Collection';
  const momentCount = collection.momentCount || 0;
  const isPrivate = collection.visibility === 'private' || result.visibility === 'private';

  return (
    <div className="rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 overflow-hidden">
      <div className="flex items-center gap-2 p-3">
        <CheckCircle className="w-4 h-4 text-purple-600 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
            Collection created!
          </p>
        </div>
        <Folder className="w-4 h-4 text-purple-600" />
      </div>

      <div className="px-3 pb-3 space-y-2">
        <p className="text-base font-medium text-purple-900 dark:text-purple-100">
          "{collectionName}"
        </p>

        <div className="flex items-center gap-3 text-xs text-purple-700 dark:text-purple-300">
          <span>{momentCount} moment{momentCount !== 1 ? 's' : ''}</span>
          <span>•</span>
          <span>{isPrivate ? '🔒 Private' : '🌐 Public'}</span>
        </div>

        {collection.description && (
          <p className="text-xs text-purple-600 dark:text-purple-400 line-clamp-2">
            {collection.description}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Tool UI for Add Moment to Collection tool
 * Shows confirmation that moment was added
 */
export function AddedToCollectionUI({ part, status }: any) {
  const result = part.result;

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
        <span className="text-xs text-blue-900 dark:text-blue-100">
          Adding to collection...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-xs font-medium text-red-900 dark:text-red-100">
            Failed to add to collection
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  const collectionName = result.collectionName || 'the collection';
  const newCount = result.momentCount || result.newCount;
  const collectionUrl = result.collectionUrl;

  return (
    <div className="rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 overflow-hidden">
      <div className="flex items-center gap-2 p-2">
        <CheckCircle className="w-3 h-3 text-green-600" />
        <p className="text-xs font-medium text-green-900 dark:text-green-100 flex-1">
          Added to "{collectionName}"
        </p>
      </div>

      {newCount && (
        <div className="px-2 pb-2">
          <p className="text-xs text-green-700 dark:text-green-300">
            Collection now has {newCount} moment{newCount !== 1 ? 's' : ''}
          </p>
        </div>
      )}

      {collectionUrl && (
        <div className="px-2 pb-2">
          <Button
            size="sm"
            variant="outline"
            className="h-6 text-xs"
            onClick={() => window.open(collectionUrl, '_blank')}
          >
            View Collection
            <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Tool UI for List Collections tool
 * Shows interactive collection picker
 */
export function CollectionPickerUI({ part, status }: any) {
  const result = part.result;
  const [selectedCollection, setSelectedCollection] = useState<string | null>(null);

  if (status === 'loading' || !result) {
    return (
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
        <span className="text-sm text-blue-900 dark:text-blue-100">
          Loading collections...
        </span>
      </div>
    );
  }

  if (!result.success) {
    return (
      <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
        <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-900 dark:text-red-100">
            Failed to load collections
          </p>
          {result.error && (
            <p className="text-xs text-red-700 dark:text-red-300 mt-1">
              {result.error}
            </p>
          )}
        </div>
      </div>
    );
  }

  const collections = result.collections || [];

  if (collections.length === 0) {
    return (
      <div className="rounded-lg bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800 p-4">
        <div className="text-center space-y-2">
          <Folder className="w-8 h-8 text-gray-400 mx-auto" />
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
            No collections yet
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Create your first collection to organize moments
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg bg-white dark:bg-gray-900/50 border border-gray-200 dark:border-gray-800 overflow-hidden">
      <div className="flex items-center gap-2 p-3 border-b border-gray-200 dark:border-gray-800">
        <Folder className="w-4 h-4 text-purple-600" />
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
          Your Collections ({collections.length})
        </p>
      </div>

      <div className="p-2 space-y-1 max-h-64 overflow-y-auto">
        {collections.map((collection: any) => (
          <button
            key={collection.id}
            onClick={() => setSelectedCollection(collection.id)}
            className={`w-full flex items-start gap-2 p-2 rounded-lg text-left transition-colors ${
              selectedCollection === collection.id
                ? 'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
            }`}
          >
            <div className="flex-shrink-0 mt-0.5">
              {selectedCollection === collection.id ? (
                <div className="w-4 h-4 rounded-full bg-purple-600 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-white" />
                </div>
              ) : (
                <div className="w-4 h-4 rounded-full border-2 border-gray-300 dark:border-gray-600" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {collection.name}
              </p>
              <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                <span>{collection.momentCount || 0} moments</span>
                {collection.visibility === 'private' && (
                  <>
                    <span>•</span>
                    <span>🔒 Private</span>
                  </>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="p-2 border-t border-gray-200 dark:border-gray-800">
        <Button
          size="sm"
          variant="outline"
          className="w-full text-xs"
          onClick={() => {
            // This would trigger a new collection creation
            console.log('[CollectionPickerUI] Create new collection clicked');
          }}
        >
          <Plus className="w-3 h-3 mr-1" />
          Create New Collection
        </Button>
      </div>
    </div>
  );
}
